import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Users, Mail, TrendingUp, Loader2, Check, X, History } from "lucide-react";

import { _t, _locale } from "@/lib/i18n";
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(_locale(), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

const TYPE_META = {
  terms: { label: _t("Termini di servizio"), icon: <ShieldCheck className="w-4 h-4 text-emerald-400" />, kind: "Obbligatorio" },
  privacy: { label: _t("Informativa sulla privacy"), icon: <ShieldCheck className="w-4 h-4 text-emerald-400" />, kind: "Obbligatorio" },
  marketing: { label: _t("Marketing e pubblicità personalizzata"), icon: <Mail className="w-4 h-4 text-white/60" />, kind: "Facoltativo" },
};

export default function AdminConsent() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/consent/stats");
      setStats(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  if (authLoading) return null;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <AppLayout>
      <AdminTabs current="consent" />

      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("Admin · Consensi")}</div>
        <h1 className="font-display text-5xl text-white leading-tight mb-2">{_t("Statistiche consensi")}</h1>
        <p className="text-white/60 max-w-2xl">{_t("Panoramica dei consensi GDPR raccolti: quanti utenti hanno accettato o rifiutato ogni tipo di consenso, la distribuzione per versione del documento e gli ultimi eventi registrati.")}</p>
      </div>

      {loading || !stats ? (
        <Loader2 className="w-8 h-8 text-[#FF5722] animate-spin mx-auto my-16" />
      ) : (
        <>
          <div className="grid md:grid-cols-4 gap-4 mb-10">
            <SummaryCard
              icon={<Users className="w-4 h-4" />}
              label={_t("Utenti totali")}
              value={stats.total_users}
              testId="total-users"
            />
            {Object.entries(stats.stats).map(([type, s]) => (
              <SummaryCard
                key={type}
                testId={`summary-${type}`}
                icon={TYPE_META[type].icon}
                label={TYPE_META[type].label}
                value={`${s.acceptance_rate}%`}
                sub={`${s.accepted} accettati · ${s.rejected} rifiutati`}
              />
            ))}
          </div>

          <div className="tc-card p-6 mb-10">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">
              <TrendingUp className="w-4 h-4" />{" "}{_t("Dettaglio per tipo di consenso")}</div>
            <div className="grid md:grid-cols-3 gap-6">
              {Object.entries(stats.stats).map(([type, s]) => (
                <ConsentDetail key={type} type={type} stats={s} total={stats.total_users} versionDist={stats.version_distribution[type]} />
              ))}
            </div>
          </div>

          <div className="tc-card p-6" data-testid="recent-events">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
              <History className="w-4 h-4" />{" "}{_t("Ultimi eventi di consenso")}</div>
            {stats.recent_events.length === 0 ? (
              <div className="text-sm text-white/40 py-4">{_t("Nessun evento.")}</div>
            ) : (
              <ul className="divide-y divide-white/5">
                {stats.recent_events.map((e) => (
                  <li key={e.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`event-${e.id}`}>
                    {e.accepted ? <Check className="w-4 h-4 text-emerald-400 shrink-0" /> : <X className="w-4 h-4 text-red-400 shrink-0" />}
                    <span className="text-white/80 truncate max-w-[240px]">{e.user_email}</span>
                    <span className="text-white/60 capitalize">{TYPE_META[e.consent_type]?.label || e.consent_type}</span>
                    <span className="text-white/40 text-xs">v{e.version}</span>
                    <span className="text-white/40 text-xs">· {e.source}</span>
                    <span className="text-white/40 text-xs ml-auto">{fmtDateTime(e.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </AppLayout>
  );
}

function SummaryCard({ icon, label, value, sub, testId }) {
  return (
    <div className="tc-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
        {icon}<span className="truncate">{label}</span>
      </div>
      <div className="font-display text-4xl text-white leading-none mb-1">{value}</div>
      {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
    </div>
  );
}

function ConsentDetail({ type, stats, total, versionDist }) {
  const meta = TYPE_META[type];
  const accepted = stats.accepted, rejected = stats.rejected;
  const acceptedPct = total ? (accepted / total) * 100 : 0;
  const rejectedPct = total ? (rejected / total) * 100 : 0;

  return (
    <div className="border border-white/10 bg-white/[0.02] rounded-lg p-4" data-testid={`detail-${type}`}>
      <div className="flex items-center gap-2 mb-3">
        {meta.icon}
        <div className="text-white text-sm font-medium">{meta.label}</div>
        <span className={`text-[9px] uppercase tracking-[0.2em] rounded px-1.5 py-0.5 border ${type === "marketing" ? "text-white/50 border-white/20" : "text-[#FF5722] border-[#FF5722]/40"}`}>
          {meta.kind}
        </span>
      </div>

      <div className="mb-3">
        <div className="flex h-2 rounded-full overflow-hidden bg-white/5">
          <div className="bg-emerald-500" style={{ width: `${acceptedPct}%` }} />
          <div className="bg-red-500/70" style={{ width: `${rejectedPct}%` }} />
        </div>
        <div className="flex justify-between text-xs mt-2">
          <span className="text-emerald-400">{_t("✓ {0} accettati ({1}%)", accepted, acceptedPct.toFixed(1))}</span>
          <span className="text-red-400">{_t("✕ {0} rifiutati ({1}%)", rejected, rejectedPct.toFixed(1))}</span>
        </div>
      </div>

      <div className="text-xs text-white/50 space-y-1">
        <div>{_t("Versione corrente:")}{" "}<span className="text-white/80">v{stats.current_version}</span></div>
        {versionDist && Object.keys(versionDist).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2 mb-1">{_t("Distribuzione per versione")}</div>
            {Object.entries(versionDist).map(([v, count]) => (
              <div key={v} className="flex justify-between">
                <span className="text-white/70">v{v}</span>
                <span className="text-white/60">{_t("{0} utenti", count)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminTabs({ current }) {
  return (
    <div className="mb-6 flex gap-2 border-b border-white/10">
      <a href="/admin/convenzioni" className={`px-4 py-3 -mb-px text-sm border-b-2 transition-colors ${current === "agreements" ? "border-[#FF5722] text-[#FF5722]" : "border-transparent text-white/60 hover:text-white"}`}>{_t("Convenzioni")}</a>
      <a href="/admin/consensi" className={`px-4 py-3 -mb-px text-sm border-b-2 transition-colors ${current === "consent" ? "border-[#FF5722] text-[#FF5722]" : "border-transparent text-white/60 hover:text-white"}`} data-testid="tab-admin-consent">{_t("Consensi")}</a>
      <a href="/admin/pagamenti" className={`px-4 py-3 -mb-px text-sm border-b-2 transition-colors ${current === "payments" ? "border-[#FF5722] text-[#FF5722]" : "border-transparent text-white/60 hover:text-white"}`} data-testid="tab-admin-payments">{_t("Pagamenti")}</a>
      <a href="/admin/utenti" className={`px-4 py-3 -mb-px text-sm border-b-2 transition-colors ${current === "users" ? "border-[#FF5722] text-[#FF5722]" : "border-transparent text-white/60 hover:text-white"}`} data-testid="tab-admin-users">{_t("Utenti")}</a>
    </div>
  );
}
